import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Button from "../components/button";
import Loader from "../components/loader";
import ProfilePicturePicker from "../components/profilePicturePicker";
import TextArea from "../components/textField/textArea";
import TextField from "../components/textField/textField";
import UnloadBlocker, { useSafeNavigate } from "../components/unloadBlocker";
import { useDashboardContext } from "../context/dashboardContext";
import { getApiUrl } from "../data/api/apiManager";
import { ArtistDTOCountryEnum, ArtistResponseDTO, ArtistResponseDTOCountryEnum } from "../openapi-generated-client";
import { deepObjectEqual, formatEnumValue } from "../utils/objectUtils";
import TextFieldDropdown from "../components/textField/textFieldDropdown";

interface BiographyEditProps {
    biographyId?: number;
    children?: any;
}

export default function BiographyEdit(props: BiographyEditProps) {

    const navigate = useSafeNavigate(useNavigate());

    const {sessionId, appContext} = useDashboardContext();
    const {artistController} = appContext;
    
    const [updateLoading, setUpdateLoading] = useState(false);
    const [error, setError] = useState<string>();

    const data = useRef<ArtistResponseDTO>(undefined);
    const newBiography = useRef<ArtistResponseDTO>(undefined);
    const [update, setUpdate] = useState(0);
    const [profilePicture, setProfilePicture] = useState<File>();

    const allCountries = Object.values(ArtistDTOCountryEnum).map(v => formatEnumValue(v));

    async function fetchBiography() {
        if(props.biographyId) {
            if(props.biographyId == -1) {
                // Create new artist
                data.current = {
                    id: -1,
                    firstName: "",
                    lastName: "",
                    profilePicture: "",
                    shortBiography: "",
                    biography: "",
                    country: undefined,
                    socialMedia: {
                        website: "",
                        instagram: "",
                        youtube: "",
                        spotify: "",
                        soundcloud: "",
                        facebook: ""
                    }
                };
            }else {
                data.current = (await artistController.getArtistProfile(props.biographyId, sessionId)).data;    
            }
            //data.current = (await biographyController.getBiographyAdmin(props.biographyId, sessionId)).data;
        } else {
            data.current = (await artistController.getOwnArtistProfile(sessionId)).data;
        }
        newBiography.current = JSON.parse(JSON.stringify(data.current));
        setUpdate(update+1);
    }

    useEffect(() => {
        fetchBiography();
    }, [props.biographyId]);

    const updateBiography = async () => {
        setUpdateLoading(true);
        var body = new FormData()
        if(profilePicture) {
            body.append('profilePicture', profilePicture, profilePicture.name);
        }
        if (newBiography.current) {
            body.append('artistDTO', new Blob([
                JSON.stringify(newBiography.current)
            ], {
                type: "application/json"
            }));
        }
        const response = await fetch(getApiUrl(props.biographyId ? props.biographyId == -1 ? "artists/admin" : "artists/admin/" + props.biographyId : "artists"), {
            method: props.biographyId ? props.biographyId == -1 ? "POST" : "PATCH" : "PUT",
            headers: {
                "authorization": sessionId
            },
            body: body
        });
        const data = await response.json();
        if(response.status == 200) {
            newBiography.current = data;
            if(props.biographyId == -1) {
                navigate("/dashboard/admin/artists");
                return;
            }
            await fetchBiography();
            setProfilePicture(undefined);
            window.scrollTo(0, 0);
        }else {
            setError(data.message);
        }
        setUpdateLoading(false);
    }

    useEffect(() => {
        setError(undefined);
    }, [update])

    if(newBiography.current == undefined || data.current == undefined) return <Loader/>;

    const noChangesMade = newBiography.current != undefined && data.current != undefined && deepObjectEqual(newBiography.current, data.current) && profilePicture == undefined;

    return <div className="inner-page bottom-margin">
            <UnloadBlocker block={!noChangesMade}/>
            {props.children}
            <TextField placeholder="Type your name..." onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.firstName = v;
                    setUpdate(update+1);
                }
            }} label="First name" value={newBiography.current.firstName}/>
            <TextField onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.lastName = v;
                    setUpdate(update+1);
                }
            }} placeholder="Your last name..." label="Last name" value={newBiography.current.lastName}/>
            <ProfilePicturePicker profilePictureContentHash={data.current? data.current.profilePicture : ""} onChange={(v: File) => {
                setProfilePicture(v);
            }}/>
            <TextArea placeholder="Who are you..." value={newBiography.current.shortBiography} maxLength={80} label="Profession / Short Description" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.shortBiography = v;
                    setUpdate(update+1);
                }
            }}/>
            <TextArea placeholder="About yourself..." value={newBiography.current.biography} maxLength={3000} label="Biography" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.biography = v;
                    setUpdate(update+1);
                }
            }}/>
            <TextFieldDropdown border title="Select your country" label="Country" options={allCountries} active={formatEnumValue(newBiography.current.country ? newBiography.current.country : ArtistDTOCountryEnum.NoSelection)} setActive={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.country = Object.values(ArtistDTOCountryEnum).find(country => formatEnumValue(country) == v);
                    setUpdate(prev => prev + 1);
                }
            }}/>
            <h2>Social Media</h2>
            <TextField placeholder="Website url" value={newBiography.current.socialMedia.website} label="Website" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.socialMedia = {
                        ...newBiography.current.socialMedia,
                        "website": v
                    }
                    setUpdate(update+1);
                }
            }}/>
            <TextField placeholder="username..." value={newBiography.current.socialMedia.instagram} label="Instagram" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.socialMedia = {
                        ...newBiography.current.socialMedia,
                        "instagram": v
                        }
                    setUpdate(update+1);
                }
            }}/>
            <TextField placeholder="username..." value={newBiography.current.socialMedia.youtube} label="Youtube" onChange={(v: string) => {
                if (newBiography.current) {

                    newBiography.current.socialMedia = {
                        ...newBiography.current.socialMedia,
                        "youtube": v
                        }
                    setUpdate(update+1);
                }
            }}/>
            <TextField placeholder="username..." value={newBiography.current.socialMedia.spotify} label="Spotify" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.socialMedia = {
                        ...newBiography.current.socialMedia,
                        "spotify": v
                        }
                    setUpdate(update+1);
                }
            }}/>
            <TextField placeholder="username..." value={newBiography.current.socialMedia.soundcloud} label="Soundcloud" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.socialMedia = {
                        ...newBiography.current.socialMedia,
                        "soundcloud": v
                        }
                    setUpdate(update+1);
                }
            }}/>
            <TextField placeholder="username..." value={newBiography.current.socialMedia.facebook} label="Facebook" onChange={(v: string) => {
                if (newBiography.current) {
                    newBiography.current.socialMedia = {
                        ...newBiography.current.socialMedia,
                        "facebook": v
                        }
                    setUpdate(update+1);
                }
            }}/>
            <p className="error">{error}</p>
            <Button loading={updateLoading} disabled={noChangesMade} onClick={() => {
                if(noChangesMade) return;
                updateBiography();
            }}>Save Changes</Button>
        </div>;
}